import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Search, Store, ChevronDown, ChevronRight, Download, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { DateRangePicker } from '@/components/DateRangePicker';
import { GeoFilters } from '@/components/GeoFilters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />{p.name}: {typeof p.value === 'number' ? `INR ${p.value.toLocaleString('en-IN')}` : p.value}
        </p>
      ))}
    </div>
  );
}

function downloadCSV(rows, filename) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => {
    const v = r[k];
    return typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? '';
  }).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function OutletDetail({ outletId, api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/outlets/${outletId}/detail`).then(r => setData(r.data)).catch(() => toast.error('Failed to load details')).finally(() => setLoading(false));
  }, [api, outletId]);

  if (loading) return <div className="flex items-center gap-2 py-6 justify-center"><Loader2 size={16} className="animate-spin text-primary" /> Loading details...</div>;
  if (!data) return null;

  const info = data.info || {};

  return (
    <div className="space-y-4 py-3 px-2">
      {/* Outlet info */}
      <div className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-muted-foreground">
        {info.classification && <span>Class: <strong className="text-foreground">{info.classification}</strong></span>}
        {info.category && <span>Category: <strong className="text-foreground">{info.category}</strong></span>}
        {info.outlet_type && <span>Type: <strong className="text-foreground">{info.outlet_type}</strong></span>}
        {info.distributor && <span>Distributor: <strong className="text-foreground">{info.distributor}</strong></span>}
        {info.phone && <span>Phone: <strong className="text-foreground">{info.phone}</strong></span>}
        {info.owner_name && <span>Owner: <strong className="text-foreground">{info.owner_name}</strong></span>}
        {info.address && <span>Address: <strong className="text-foreground">{info.address}</strong></span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly trend */}
        {data.monthly?.length > 0 && (
          <div className="bg-muted/30 border border-border rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-heading font-semibold">Monthly Order Trend</h4>
              <button onClick={() => downloadCSV(data.monthly, `outlet-${outletId}-monthly`)} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"><Download size={11} /> CSV</button>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} tickFormatter={m => m?.slice(2)} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={v => formatINR(v)} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="order_amount" name="Order Value" fill="#1B3A8C" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="fulfilled_amount" name="Fulfilled" fill="#10B981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* SKU breakdown */}
        {data.skus?.length > 0 && (
          <div className="bg-muted/30 border border-border rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-heading font-semibold">Top SKUs Ordered</h4>
              <button onClick={() => downloadCSV(data.skus, `outlet-${outletId}-skus`)} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"><Download size={11} /> CSV</button>
            </div>
            <div className="max-h-40 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead className="text-[10px] sticky top-0 bg-muted/30">SKU</TableHead><TableHead className="text-[10px] text-right sticky top-0 bg-muted/30">Amount</TableHead><TableHead className="text-[10px] text-right sticky top-0 bg-muted/30">Qty</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {data.skus.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs py-1">{s.sku_name}</TableCell>
                      <TableCell className="text-xs text-right py-1">INR {s.amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-xs text-right py-1">{s.qty?.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Visit history */}
      {data.visits?.length > 0 && (
        <div className="bg-muted/30 border border-border rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-heading font-semibold">Visit History (TC)</h4>
            <button onClick={() => downloadCSV(data.visits, `outlet-${outletId}-visits`)} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"><Download size={11} /> CSV</button>
          </div>
          <div className="max-h-36 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] sticky top-0 bg-muted/30">Date</TableHead>
                  <TableHead className="text-[10px] sticky top-0 bg-muted/30">FE</TableHead>
                  <TableHead className="text-[10px] sticky top-0 bg-muted/30">Order?</TableHead>
                  <TableHead className="text-[10px] text-right sticky top-0 bg-muted/30">Amount</TableHead>
                  <TableHead className="text-[10px] sticky top-0 bg-muted/30">Time</TableHead>
                  <TableHead className="text-[10px] sticky top-0 bg-muted/30">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.visits.map((v, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs py-1">{v.date}</TableCell>
                    <TableCell className="text-xs py-1">{v.fe_name}</TableCell>
                    <TableCell className="text-xs py-1"><span className={v.order_placed === 'Yes' ? 'text-green-600' : 'text-red-500'}>{v.order_placed || '-'}</span></TableCell>
                    <TableCell className="text-xs text-right py-1">{v.amount > 0 ? `INR ${v.amount.toLocaleString('en-IN')}` : '-'}</TableCell>
                    <TableCell className="text-xs py-1">{v.time_spent || '-'}</TableCell>
                    <TableCell className="text-xs py-1 text-muted-foreground max-w-[120px] truncate">{v.reason || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OutletLookupPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ outlets: [], total: 0, page: 1, pages: 0 });
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('order_amount');
  const [order, setOrder] = useState('desc');
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: 50, sort, order };
    if (search) params.search = search;
    if (zone) params.zone = zone;
    if (subzone) params.subzone = subzone;
    if (state) params.state = state;
    if (city) params.city = city;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (showAll) params.show_all = true;
    api.get('/outlets/lookup', { params }).then(r => setData(r.data)).catch(() => toast.error('Failed to load outlets')).finally(() => setLoading(false));
  }, [api, search, zone, subzone, state, city, dateFrom, dateTo, page, sort, order, showAll]);

  useEffect(() => { load(); }, [load]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleSort = (col) => {
    if (sort === col) setOrder(o => o === 'desc' ? 'asc' : 'desc');
    else { setSort(col); setOrder('desc'); }
    setPage(1);
  };

  const SortHeader = ({ col, children, className }) => (
    <TableHead className={`text-xs cursor-pointer select-none hover:text-foreground ${className || ''}`} onClick={() => toggleSort(col)}>
      <span className="flex items-center gap-1">{children} <ArrowUpDown size={11} className={sort === col ? 'text-primary' : 'text-muted-foreground/40'} /></span>
    </TableHead>
  );

  return (
    <div className="space-y-5" data-testid="outlet-lookup-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Outlet Lookup</h1>
        <p className="text-sm text-muted-foreground mt-1">Search and explore outlet-wise sales, SKU details, and visit history</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search outlet name or ID..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-9 h-10"
            data-testid="outlet-search"
          />
        </div>
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone} state={state} setState={setState} city={city} setCity={setCity} onReset={() => setPage(1)} />
        <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }} />
        <button
          onClick={() => { setShowAll(s => !s); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border transition-colors ${showAll ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'}`}
          data-testid="show-all-toggle"
        >
          <Store size={13} /> {showAll ? 'All Outlets' : 'With Orders'}
        </button>
        {data.total > 0 && (
          <button onClick={() => downloadCSV(data.outlets, 'outlet-sales')} className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border border-border bg-card text-muted-foreground hover:bg-muted transition-colors" data-testid="download-outlets-csv">
            <Download size={13} /> Export CSV
          </button>
        )}
      </div>

      {/* Results summary */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {loading ? 'Loading...' : `${data.total?.toLocaleString('en-IN')} outlets found`}
          {search && <span> matching "<strong className="text-foreground">{search}</strong>"</span>}
        </p>
        {data.pages > 1 && (
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-2 py-1 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted" data-testid="prev-page">Prev</button>
            <span className="text-xs text-muted-foreground">{page} / {data.pages}</span>
            <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages} className="px-2 py-1 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted" data-testid="next-page">Next</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs w-8"></TableHead>
                <SortHeader col="outlet_name">Outlet</SortHeader>
                <TableHead className="text-xs">Beat</TableHead>
                <TableHead className="text-xs">Zone</TableHead>
                <TableHead className="text-xs">FE</TableHead>
                <SortHeader col="order_amount" className="text-right">Order Value</SortHeader>
                <SortHeader col="fulfilled_amount" className="text-right">Fulfilled</SortHeader>
                <SortHeader col="order_count" className="text-right">Orders</SortHeader>
                <SortHeader col="sku_count" className="text-right">SKUs</SortHeader>
                <TableHead className="text-xs">Last Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.outlets.map(o => (
                <> 
                  <TableRow
                    key={o.outlet_id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setExpanded(expanded === o.outlet_id ? null : o.outlet_id)}
                    data-testid={`outlet-row-${o.outlet_id}`}
                  >
                    <TableCell className="py-2 px-2">
                      {expanded === o.outlet_id ? <ChevronDown size={14} className="text-primary" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="font-medium text-sm">{o.outlet_name}</div>
                      <div className="text-[10px] text-muted-foreground">{o.outlet_id}</div>
                    </TableCell>
                    <TableCell className="text-xs py-2 text-muted-foreground">{o.beat}</TableCell>
                    <TableCell className="text-xs py-2 text-muted-foreground">{o.zone}</TableCell>
                    <TableCell className="text-xs py-2 text-muted-foreground">{o.fe_name}</TableCell>
                    <TableCell className="text-sm text-right py-2 font-medium">INR {o.order_amount?.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-sm text-right py-2">INR {o.fulfilled_amount?.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-sm text-right py-2">{o.order_count?.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-sm text-right py-2">{o.sku_count}</TableCell>
                    <TableCell className="text-xs py-2 text-muted-foreground">{o.last_month}</TableCell>
                  </TableRow>
                  {expanded === o.outlet_id && (
                    <TableRow key={`${o.outlet_id}-detail`}>
                      <TableCell colSpan={10} className="p-0 bg-muted/20">
                        <OutletDetail outletId={o.outlet_id} api={api} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
              {!loading && data.outlets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12">
                    <Store size={32} strokeWidth={1} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">{search ? 'No outlets match your search' : 'No outlet data available'}</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Bottom pagination */}
      {data.pages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1.5 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted">Previous</button>
          <span className="px-3 py-1.5 text-xs text-muted-foreground">Page {page} of {data.pages}</span>
          <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages} className="px-3 py-1.5 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted">Next</button>
        </div>
      )}
    </div>
  );
}
